import { useRef, useState } from 'react';
import { Bookmark, BookmarkPlus, Trash2, X } from 'lucide-react';
import { useSavedViews } from '@/hooks/useSavedViews';
import { usePermission } from '@/hooks/usePermission';
import { Permissions } from '@/constants/permissions';
import { Spinner } from '@/components/ui/Spinner';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { cn } from '@/utils/cn';

interface SavedViewBarProps {
  reportKey: string;
  currentFilters: unknown;
  onApply: (filters: unknown) => void;
}

const INPUT_CLS =
  'w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-1.5 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-brand-500';
const LABEL_CLS = 'block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1';

/**
 * Compact saved-view bar for report pages: pick a saved view (applies its
 * filters), save the current filters as a new view, or delete an own view.
 */
export const SavedViewBar = ({ reportKey, currentFilters, onApply }: SavedViewBarProps) => {
  const { views, loading, save, remove } = useSavedViews(reportKey);
  const { hasPermission } = usePermission();
  const canShare = hasPermission(Permissions.RPT_MANAGE_SHARED_VIEWS);

  const [selectedId, setSelectedId] = useState('');
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [name, setName] = useState('');
  const [asDefault, setAsDefault] = useState(false);
  const [asShared, setAsShared] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);

  const myViews = views.filter(v => v.isOwn);
  const sharedViews = views.filter(v => !v.isOwn && v.isShared);
  const selectedView = views.find(v => v.id === selectedId);

  const handleSelect = (id: string) => {
    setSelectedId(id);
    const view = views.find(v => v.id === id);
    if (view) onApply(view.parsedFilters);
  };

  const handleSave = async () => {
    const trimmed = name.trim();
    if (!trimmed) { setSaveError('Enter a view name.'); return; }
    setSaving(true);
    setSaveError('');
    try {
      await save(trimmed, currentFilters, { shared: canShare && asShared, isDefault: asDefault });
      setPopoverOpen(false);
      setName('');
      setAsDefault(false);
      setAsShared(false);
    } catch {
      setSaveError('Failed to save view. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = () => {
    if (!selectedView || !selectedView.isOwn) return;
    setDeleteConfirmOpen(true);
  };

  const executeDelete = async () => {
    if (!selectedView) return;
    setDeleting(true);
    try {
      await remove(selectedView.id);
      setSelectedId('');
      setDeleteConfirmOpen(false);
    } catch {
      // deletion failure surfaces on next reload; keep the bar usable
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="relative flex flex-wrap items-center gap-2">
      <Bookmark size={14} className="text-gray-400" />

      <select
        value={selectedId}
        onChange={e => handleSelect(e.target.value)}
        disabled={loading}
        className={cn(INPUT_CLS, 'w-auto min-w-[180px]')}
        title="Apply a saved view"
      >
        <option value="">Saved views…</option>
        {myViews.length > 0 && (
          <optgroup label="My views">
            {myViews.map(v => (
              <option key={v.id} value={v.id}>
                {v.name}{v.isDefault ? ' (default)' : ''}
              </option>
            ))}
          </optgroup>
        )}
        {sharedViews.length > 0 && (
          <optgroup label="Shared">
            {sharedViews.map(v => (
              <option key={v.id} value={v.id}>
                {v.name}{v.ownerName ? ` — ${v.ownerName}` : ''}
              </option>
            ))}
          </optgroup>
        )}
      </select>

      {selectedView?.isOwn && (
        <button
          onClick={handleDelete}
          className="p-1.5 rounded-lg text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
          title="Delete this saved view"
        >
          <Trash2 size={14} />
        </button>
      )}

      <button
        onClick={() => setPopoverOpen(o => !o)}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
        title="Save current filters as a view"
      >
        <BookmarkPlus size={14} />
        Save view…
      </button>

      {loading && <Spinner size="sm" />}

      {popoverOpen && (
        <div
          ref={popoverRef}
          className="absolute left-0 top-full z-20 mt-2 w-72 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 shadow-lg"
        >
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-semibold text-gray-800 dark:text-gray-100">Save view</span>
            <button
              onClick={() => setPopoverOpen(false)}
              className="p-1 rounded text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              title="Close"
            >
              <X size={14} />
            </button>
          </div>

          <label className={LABEL_CLS}>View name</label>
          <input
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') void handleSave(); }}
            placeholder="e.g. Month-end review"
            className={INPUT_CLS}
            autoFocus
          />

          <label className="mt-3 flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
            <input
              type="checkbox"
              checked={asDefault}
              onChange={e => setAsDefault(e.target.checked)}
              className="rounded border-gray-300 dark:border-gray-600 text-brand-600 focus:ring-brand-500"
            />
            Set as default
          </label>

          {canShare && (
            <label className="mt-2 flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
              <input
                type="checkbox"
                checked={asShared}
                onChange={e => setAsShared(e.target.checked)}
                className="rounded border-gray-300 dark:border-gray-600 text-brand-600 focus:ring-brand-500"
              />
              Share with company
            </label>
          )}

          {saveError && <p className="mt-2 text-xs text-red-600 dark:text-red-400">{saveError}</p>}

          <div className="mt-4 flex justify-end gap-2">
            <button
              onClick={() => setPopoverOpen(false)}
              className="px-3 py-1.5 rounded-lg text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              onClick={() => { void handleSave(); }}
              disabled={saving}
              className="px-3 py-1.5 rounded-lg text-sm font-medium bg-brand-600 text-white hover:bg-brand-700 disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={deleteConfirmOpen}
        variant="delete"
        title="Delete Saved View"
        message={<span>Delete saved view <strong>{selectedView?.name}</strong>?</span>}
        confirmLabel="Yes, Delete"
        cancelLabel="Cancel"
        loading={deleting}
        onConfirm={() => { void executeDelete(); }}
        onCancel={() => setDeleteConfirmOpen(false)}
      />
    </div>
  );
};
